// TMOS Log File Viewer Page
// Date: 2025-11-17
// Purpose: Platform Admin page for viewing and managing log files

import React, { useState, useEffect } from 'react';
import { 
  DocumentTextIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowPathIcon,
  TrashIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { logFileService, LogFile, LogFileContent, LogFileStats } from '@/services/logFileService';
import { useLanguage } from '@/state/language/LanguageProvider';
import { toast } from 'react-hot-toast';

export const LogFileViewerPage: React.FC = () => {
  const { t } = useLanguage();
  const [logFiles, setLogFiles] = useState<LogFile[]>([]);
  const [selectedFile, setSelectedFile] = useState<string>('');
  const [logContent, setLogContent] = useState<LogFileContent | null>(null);
  const [logStats, setLogStats] = useState<LogFileStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingContent, setLoadingContent] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [tailLines, setTailLines] = useState<number>(1000);
  const [showCleanup, setShowCleanup] = useState(false);
  const [cleanupDays, setCleanupDays] = useState<number>(30);

  useEffect(() => {
    loadLogFiles();
  }, []);

  useEffect(() => {
    if (selectedFile) {
      loadLogFile();
    }
  }, [selectedFile, searchTerm, tailLines]);

  const loadLogFiles = async () => {
    try {
      setLoading(true);
      const files = await logFileService.listLogFiles();
      setLogFiles(files);
      if (files.length > 0 && !selectedFile) {
        // Select the most recent file (first in list)
        setSelectedFile(files[0].filename);
      }
    } catch (error: any) {
      console.error('Error loading log files:', error);
      toast.error(error.response?.data?.message || 'Failed to load log files');
    } finally {
      setLoading(false);
    }
  };

  const loadLogFile = async () => {
    if (!selectedFile) return;

    try {
      setLoadingContent(true);
      const content = await logFileService.getLogFile(selectedFile, {
        tail: tailLines,
        search: searchTerm || undefined
      });
      setLogContent(content);

      // Load stats
      const stats = await logFileService.getLogFileStats(selectedFile);
      setLogStats(stats);
    } catch (error: any) {
      console.error('Error loading log file:', error);
      toast.error(error.response?.data?.message || 'Failed to load log file');
    } finally {
      setLoadingContent(false);
    }
  };

  const handleDownload = async () => {
    if (!selectedFile) return;

    try {
      const blob = await logFileService.downloadLogFile(selectedFile);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = selectedFile;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Log file downloaded');
    } catch (error: any) {
      console.error('Error downloading log file:', error);
      toast.error('Failed to download log file');
    }
  };

  const handleDelete = async (filename: string) => {
    if (!confirm(`Are you sure you want to delete ${filename}? This action cannot be undone.`)) {
      return;
    }

    try {
      await logFileService.deleteLogFile(filename);
      toast.success('Log file deleted');
      if (selectedFile === filename) {
        setSelectedFile('');
        setLogContent(null);
      }
      loadLogFiles();
    } catch (error: any) {
      console.error('Error deleting log file:', error);
      toast.error(error.response?.data?.message || 'Failed to delete log file');
    }
  };

  const handleCleanup = async () => {
    if (!confirm(`Delete all log files older than ${cleanupDays} days? This action cannot be undone.`)) {
      return;
    }

    try {
      const result = await logFileService.cleanupOldLogs(cleanupDays);
      toast.success(`Cleanup completed: ${result.deleted} files deleted`);
      loadLogFiles();
      setShowCleanup(false);
    } catch (error: any) {
      console.error('Error cleaning up logs:', error);
      toast.error(error.response?.data?.message || 'Failed to cleanup logs');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div className="min-h-screen bg-[var(--color-surface-bg)]">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <DocumentTextIcon className="w-8 h-8 text-[var(--color-primary-bg)]" />
              <div>
                <h1 className="text-3xl font-bold text-[var(--color-surface-text)]">
                  {t('admin.logs.title') || 'Log File Viewer'}
                </h1>
                <p className="text-[var(--color-secondary-text)]">
                  {t('admin.logs.subtitle') || 'View and manage system log files'}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowCleanup(!showCleanup)}
              >
                <TrashIcon className="w-5 h-5 mr-2" />
                {t('admin.logs.cleanup') || 'Cleanup'}
              </Button>
              <Button
                variant="outline"
                onClick={loadLogFiles}
              >
                <ArrowPathIcon className="w-5 h-5 mr-2" />
                {t('common.refresh') || 'Refresh'}
              </Button>
            </div>
          </div>
        </div>

        {/* Cleanup Panel */}
        {showCleanup && (
          <Card variant="outline" padding="md" className="mb-6">
            <CardContent>
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium text-[var(--color-secondary-text)]">
                  Delete logs older than (days):
                </label>
                <input
                  type="number"
                  min="1"
                  value={cleanupDays}
                  onChange={(e) => setCleanupDays(parseInt(e.target.value) || 30)}
                  className="w-24 px-3 py-2 border rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)]"
                />
                <Button
                  variant="primary"
                  onClick={handleCleanup}
                >
                  {t('admin.logs.deleteOld') || 'Delete Old Logs'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowCleanup(false)}
                >
                  {t('common.cancel') || 'Cancel'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* File List */}
          <Card variant="outline" padding="none" className="lg:col-span-1">
            <CardContent className="p-0">
              <div className="bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] p-4">
                <h2 className="font-semibold">{t('admin.logs.files') || 'Log Files'}</h2>
              </div>
              <div className="max-h-[70vh] overflow-y-auto">
                {loading ? (
                  <div className="p-4 text-center text-[var(--color-secondary-text)]">
                    {t('common.loading') || 'Loading...'}
                  </div>
                ) : logFiles.length === 0 ? (
                  <div className="p-4 text-center text-[var(--color-secondary-text)]">
                    {t('admin.logs.noFiles') || 'No log files found'}
                  </div>
                ) : (
                  <div className="divide-y divide-[var(--color-border)]">
                    {logFiles.map((file) => (
                      <div
                        key={file.filename}
                        className={`p-3 cursor-pointer hover:bg-[var(--color-secondary-bg)] ${
                          selectedFile === file.filename ? 'bg-[var(--color-secondary-bg)]' : ''
                        }`}
                        onClick={() => setSelectedFile(file.filename)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[var(--color-surface-text)] truncate">
                              {file.filename}
                            </p>
                            <p className="text-xs text-[var(--color-secondary-text)]">
                              {formatFileSize(file.size)} • {new Date(file.lastModified).toLocaleDateString()}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(file.filename);
                            }}
                            className="ml-2"
                          >
                            <TrashIcon className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Log Content */}
          <Card variant="outline" padding="none" className="lg:col-span-3">
            <CardContent className="p-0">
              {/* Toolbar */}
              <div className="bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] p-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 flex-1">
                  <select
                    value={selectedFile}
                    onChange={(e) => setSelectedFile(e.target.value)}
                    className="px-3 py-2 rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border border-[var(--color-border)]"
                  >
                    <option value="">{t('admin.logs.selectFile') || 'Select a log file'}</option>
                    {logFiles.map((file) => (
                      <option key={file.filename} value={file.filename}>
                        {file.filename}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    min="100"
                    max="10000"
                    value={tailLines}
                    onChange={(e) => setTailLines(parseInt(e.target.value) || 1000)}
                    className="w-24 px-3 py-2 rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border border-[var(--color-border)]"
                    placeholder="Lines"
                  />
                  <div className="flex items-center gap-2 flex-1 max-w-md">
                    <MagnifyingGlassIcon className="w-5 h-5" />
                    <input
                      type="text"
                      placeholder={t('admin.logs.searchPlaceholder') || 'Search in logs...'}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="flex-1 px-3 py-2 rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border border-[var(--color-border)]"
                    />
                    {searchTerm && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSearchTerm('')}
                      >
                        <XMarkIcon className="w-5 h-5" />
                      </Button>
                    )}
                  </div>
                </div>
                {selectedFile && (
                  <Button
                    variant="outline"
                    onClick={handleDownload}
                  >
                    <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
                    {t('common.download') || 'Download'}
                  </Button>
                )}
              </div>

              {/* Stats */}
              {logStats && (
                <div className="bg-[var(--color-secondary-bg)] px-4 py-2 text-sm text-[var(--color-secondary-text)] flex items-center gap-6">
                  <span>Size: {formatFileSize(logStats.size)}</span>
                  <span>Lines: {logStats.lines.toLocaleString()}</span>
                  {logStats.firstLineDate && (
                    <span>First: {logStats.firstLineDate}</span>
                  )}
                  {logStats.lastLineDate && (
                    <span>Last: {logStats.lastLineDate}</span>
                  )}
                </div>
              )}

              {/* Log Content */}
              {loadingContent ? (
                <div className="text-center py-12">
                  <p className="text-[var(--color-secondary-text)]">{t('common.loading') || 'Loading...'}</p>
                </div>
              ) : logContent ? (
                <div className="relative">
                  <pre className="p-4 overflow-auto max-h-[60vh] bg-[#1e1e1e] text-[#d4d4d4] font-mono text-sm whitespace-pre-wrap break-words">
                    {logContent.content || t('admin.logs.noContent') || 'No log content available'}
                  </pre>
                  {searchTerm && (
                    <div className="absolute top-2 right-2 bg-yellow-500 text-black px-2 py-1 rounded text-xs">
                      Showing filtered results
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <DocumentTextIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                  <p className="text-[var(--color-secondary-text)]">
                    {t('admin.logs.selectFileToView') || 'Select a log file to view'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

